import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  styled,
  TextField,
  Button as MuiButton,
  Pagination,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProductGroupResponse } from "src/models/product/ProductGroupResponse";
import { ProductGroupRow } from "./ProductGroupRow";
import { fetchProductGroupAsync } from "src/infrastructure/http/appApiService";
import { useCountry } from "src/components/Context/CountryContext";
import { EmptyListState } from "src/components/EmptyState/EmptyListState";
import { SearchOutlined } from "@mui/icons-material";
import DeleteIconOutlined from "@mui/icons-material/DeleteOutlined";
import { ValidationLabel } from "src/components/Form";

const AutoCompleteTextField = styled(TextField)`
  .MuiInputBase-root {
    border-radius: 0;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`;

const CustomRemoveButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: "transparent !important",
  color: theme.palette.error.main,
  textTransform: "none",
  padding: "6px 16px",
}));

type ProductGroupSelectProps = {
  name: string;
  value: number[];
  onChange: (value: number[]) => void;
  hasError?: boolean;
  errorMessage?: string;
};

export const ProductGroupSelect: React.FC<ProductGroupSelectProps> = ({
  name,
  value,
  onChange,
  hasError,
  errorMessage,
}: ProductGroupSelectProps) => {
  const { country } = useCountry();

  const [productGroupOptions, setProductGroupOptions] = useState<ProductGroupResponse[]>([]);
  const [filteredProductGroups, setFilteredProductGroups] = useState<ProductGroupResponse[]>([]);
  const [productGroupSearchValue, setProductGroupSearchValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>(errorMessage);
  const [selectedProductGroups, setSelectedProductGroups] = useState<ProductGroupResponse[]>([]);

  const [page, setPage] = useState(1);
  const pageSize =  10;
  const totalCount = selectedProductGroups.length;

  const paginatedProductGroups = useMemo(() => {
    const start = (page - 1) * pageSize;
    return selectedProductGroups.slice(start, start + pageSize);
  }, [selectedProductGroups, page]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalCount, page]);

  useEffect(() => {
    setIsLoading(true);
    fetchProductGroupAsync(country)
      .then((results) => {
        setProductGroupOptions(results ?? []);
        setIsLoading(false);
      })
      .catch(() => {
        setProductGroupOptions([]);
        setIsLoading(false);
        setValidationMessage("Failed to load Product Groups. Please try again later.");
      });
  }, [country]);

  useEffect(() => {
    if (!productGroupSearchValue) {
      setFilteredProductGroups([]);
      return;
    }
    const search = productGroupSearchValue.toLowerCase();
    setFilteredProductGroups(
      productGroupOptions.filter(
        (pg) => pg.description?.toLowerCase().includes(search) || pg.id?.includes(search)
      )
    );
  }, [productGroupSearchValue, productGroupOptions]);

  useEffect(() => {
    if (!value || productGroupOptions.length === 0) {
      setSelectedProductGroups([]);
      return;
    }
    const processedValue = value.map(String);
    const selected = productGroupOptions.filter((pg) => processedValue.includes(pg.id));
    setSelectedProductGroups(selected);
  }, [value, productGroupOptions]);

  const handleProductAdded = useCallback(
    (newValue: ProductGroupResponse) => {
      const newProductGroupIds = [...value, parseInt(newValue.id)];
      onChange(newProductGroupIds);
      setProductGroupSearchValue("");
    },
    [value, onChange]
  );

  const handleProductRemoved = useCallback(
    (productGroup: ProductGroupResponse) => {
      const newProductGroupIds = value.filter((group) => group !== parseInt(productGroup.id));
      onChange(newProductGroupIds);
    },
    [value, onChange]
  );

  const handleClearAllSkus = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2} marginBottom={2} marginTop={0} justifyContent="center">
          <Autocomplete
            id={`productgroup-autocomplete-${name}`}
            open={showOptions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
            filterOptions={(x) => x}
            options={productGroupSearchValue == "" ? productGroupOptions : filteredProductGroups}
            autoComplete
            inputValue={productGroupSearchValue}
            filterSelectedOptions
            loading={isLoading}
            loadingText="Searching for Product Goups..."
            noOptionsText="No Product Groups found"
            style={{ boxShadow: "2px 2px 0 0 var(--palette-primary-shadow__1yqyf5p6)", flexGrow: 1 }}
            onInputChange={(_, newInputValue) => {
              setProductGroupSearchValue(newInputValue);
              setShowOptions(true);
            }}
            onFocus={() => setShowOptions(true)}
            onBlur={() => setShowOptions(false)}
            renderInput={(params) => <AutoCompleteTextField {...params} placeholder="Search by Product Group" fullWidth />}
            renderOption={(_, option) => {
              const isAdded = value.includes(parseInt(option.id));
              return (
                <ProductGroupRow
                  key={option.id}
                  productGroup={option}
                  isAdded={isAdded}
                  onClick={isAdded ? handleProductRemoved : handleProductAdded}
                />
              );
            }}
            slotProps={{
              popper: {
                disablePortal: true,
                placement: "bottom",
              },
            }}
          />
        </Stack>

        {value.length > 0 ? (
          <Box padding={3} border={1} borderColor={(theme) => theme.palette.grey[400]}>
            {isLoading && selectedProductGroups.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                <CircularProgress />
              </Box>
            ) : (
              paginatedProductGroups.map((productGroup) => (
                <ProductGroupRow
                  key={productGroup.id}
                  productGroup={productGroup}
                  isAdded={true}
                  onClick={handleProductRemoved}
                />
              ))
            )}
          </Box>
        ) : (
          <EmptyListState
            icon={SearchOutlined}
            title="No Product Groups added"
            message="Please add Product Groups by searching from the field above"
          />
        )}

        {selectedProductGroups.length > 0 && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <CustomRemoveButton
              startIcon={<DeleteIconOutlined style={{ color: "#d32f2f" }} />}
              onClick={handleClearAllSkus}
              disabled={isLoading || selectedProductGroups.length === 0}
            >
              Remove all Product Groups
            </CustomRemoveButton>

            {totalCount > pageSize && (
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                disabled={isLoading}
              />
            )}
            <div style={{ width: "175px" }}></div>
          </Stack>
        )}
      </Stack>
      <ValidationLabel errorMessage={validationMessage} />
    </>
  );
};
